import { ChangeDetectionStrategy, Component, computed, input, output } from '@angular/core';
import { MeetingArtifact, MeetingDto } from '@interfaces/meetings/responses/meeting.response';
import { SharedModule } from '@src/app/shared/shared.module';
import { rotuloDoTipoDaCall } from '@features/dashboard/meetings/components/meeting-presenter';
import { MeetingStatusTagComponent } from '@features/dashboard/meetings/components/meeting-status-tag/meeting-status-tag.component';
import {
  formatarDuracaoDaLigacao,
  iniciaisDe,
  nomeDoParticipante,
  nomesDasAgendas,
} from '@features/dashboard/meetings/components/meeting-presenter';
import { ActionMenuItem } from '@src/app/shared/components/action-menu/action-menu.component';
import {
  MeetingLifecycleAction,
  meetingLifecycleOptions,
} from '@features/dashboard/meetings/components/meeting-lifecycle-actions';
import {
  AUDIENCE_ICONS,
  AUDIENCE_LABELS,
  AUDIENCE_ORDER,
  audienceState,
} from '@features/dashboard/meetings/components/meeting-visibility-presenter';
import { MeetingTitleEditorComponent } from '@features/dashboard/meetings/pages/meeting-detail/components/meeting-title-editor/meeting-title-editor.component';

/** Situações de reunião que ainda não aconteceu: o link da call vale. */
const AINDA_NAO_ACONTECEU: readonly string[] = ['Scheduled', 'BotCancelled'];
/** Quantos avatares aparecem antes do "+N". */
const AVATARES_VISIVEIS = 3;
/** Nome de participante que é bot de gravação ("Cog AI bot (Staging)", "Fireflies.ai Notetaker ..."). */
const EH_BOT = /\bbot\b|notetaker/i;

/**
 * Cabeçalho da tela de detalhe: título, "data · hora · duração", avatares de quem participou, situação,
 * selo de acesso e as ações.
 *
 * - **Título e visibilidade (card 3803):** título com lápis de renomear, descrição curta e título
 *   original (`app-meeting-title-editor`), e o selo "Visível para Time · Parceiro · Cliente", que abre a
 *   janela "Visibilidade da call". Os selos antigos "Acesso geral", "Time Legatus" e "Privada" saíram.
 *
 * - **Compartilhar** (acesso geral) e **Voltar a privada** só para quem participou e na reunião que ainda não
 *   está aberta ao time (`canManageAccess`; regra de 07/10/2026), e só na rota da visão geral (`podeAgir`).
 * - **Baixar vídeo** é a ação principal quando há gravação.
 * - O menu **…** junta o que é de bastidor: Vincular e os arquivos JSON crus da Recall ("Dados
 *   técnicos"), que antes ficavam soltos na coluna lateral.
 * - **Arquivar / Restaurar** (`MEETING_ARCHIVE`) e **Excluir** (`MEETING_DELETE` + `canDelete`) no fim do
 *   menu **…** (card 3786). Quem confirma e chama a API é a tela.
 */
@Component({
  selector: 'app-meeting-detail-header',
  standalone: true,
  imports: [SharedModule, MeetingStatusTagComponent, MeetingTitleEditorComponent],
  templateUrl: './meeting-detail-header.component.html',
  styleUrl: './meeting-detail-header.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class MeetingDetailHeaderComponent {
  readonly meeting = input.required<MeetingDto>();
  /** Time interno pela visão geral (não pela aba do deal). */
  readonly podeAgir = input(false);
  readonly ocupada = input(false);
  readonly baixando = input(false);
  /** `MEETING_ARCHIVE` de quem está logado (card 3786). */
  readonly canArchive = input(false);
  /** `MEETING_DELETE` de quem está logado; Excluir ainda exige `canDelete` da reunião (card 3786). */
  readonly canDelete = input(false);

  readonly baixar = output<void>();
  readonly alternarAcesso = output<void>();
  /** Abre a janela Compartilhar (card 3241): pessoas, link e acessos. */
  readonly compartilhar = output<void>();
  readonly vincular = output<void>();
  readonly abrirArtefato = output<MeetingArtifact>();
  /** Arquivar, restaurar ou excluir (card 3786). */
  readonly lifecycleAction = output<MeetingLifecycleAction>();
  /** A reunião mudou aqui dentro (renomeada, card 3803): a tela troca pela que voltou. */
  readonly meetingUpdated = output<MeetingDto>();
  /** Abre a janela "Visibilidade da call" (card 3803). */
  readonly openVisibility = output<void>();

  /**
   * Selo "Visível para Time · Parceiro · Cliente" (card 3803, opção C): o que está desligado aparece
   * riscado. Sem `visibility` (visão do parceiro pela aba do deal) o selo não aparece.
   */
  protected readonly sealItems = computed(() => {
    const visibility = this.meeting().visibility;
    if (!visibility) return [];
    return AUDIENCE_ORDER.map(audience => ({
      label: AUDIENCE_LABELS[audience],
      icon: AUDIENCE_ICONS[audience],
      visible: audienceState(visibility, audience).visible,
    }));
  });

  /** Texto do `title` do selo: o motivo de cada chave, como o backend escreveu. */
  protected readonly sealTooltip = computed(() => {
    const visibility = this.meeting().visibility;
    if (!visibility) return '';
    return AUDIENCE_ORDER.map(
      audience => `${AUDIENCE_LABELS[audience]}: ${audienceState(visibility, audience).reason}`
    ).join('\n');
  });

  protected readonly duracao = formatarDuracaoDaLigacao;

  protected readonly aindaNaoAconteceu = computed(() =>
    AINDA_NAO_ACONTECEU.includes(this.meeting().status)
  );

  /** "Victor e Breno": de quem é a agenda que trouxe a reunião (card 3191). */
  protected readonly agendas = computed(() => nomesDasAgendas(this.meeting().calendarOwners));

  /** Tipo da reunião classificado pelo Cog ("Operação de cliente", "Recrutamento"...), ou nulo. */
  protected readonly tipoDaCall = computed(() => rotuloDoTipoDaCall(this.meeting().callType));

  /**
   * Quem entrou; sem presença registrada (reunião futura), os convidados. Bots de gravação (o nosso
   * "Cog AI bot", notetakers de terceiros) não são pessoas e ficam de fora dos avatares.
   */
  private readonly pessoas = computed(() => {
    const participantes = (this.meeting().participants ?? []).filter(
      p => !EH_BOT.test(nomeDoParticipante(p))
    );
    const presentes = participantes.filter(p => p.attended);
    return (presentes.length ? presentes : participantes).map(nomeDoParticipante);
  });

  protected readonly avatares = computed(() =>
    this.pessoas()
      .slice(0, AVATARES_VISIVEIS)
      .map(nome => ({ nome, iniciais: iniciaisDe(nome) }))
  );

  protected readonly avataresOcultos = computed(() =>
    Math.max(0, this.pessoas().length - AVATARES_VISIVEIS)
  );

  protected readonly nomesDasPessoas = computed(() => this.pessoas().join(', '));

  protected readonly acoesDoMenu = computed<ActionMenuItem[]>(() => {
    const m = this.meeting();
    if (!this.podeAgir()) return [];

    const itens: ActionMenuItem[] = [
      { label: 'Vincular', icon: 'pi pi-link', command: () => this.vincular.emit() },
    ];

    const artefatos: ActionMenuItem[] = [];
    if (m.hasParticipantEvents) {
      artefatos.push({
        label: 'Eventos dos participantes (JSON)',
        icon: 'pi pi-download',
        command: () => this.abrirArtefato.emit('participant-events'),
      });
    }
    if (m.hasSpeakerTimeline) {
      artefatos.push({
        label: 'Quem falou quando (JSON)',
        icon: 'pi pi-download',
        command: () => this.abrirArtefato.emit('speaker-timeline'),
      });
    }
    if (m.hasMeetingMetadata) {
      artefatos.push({
        label: 'Metadados da reunião (JSON)',
        icon: 'pi pi-download',
        command: () => this.abrirArtefato.emit('meeting-metadata'),
      });
    }
    if (artefatos.length) {
      itens.push({ separator: true }, ...artefatos);
    }

    const lifecycle = meetingLifecycleOptions(m, {
      canArchive: this.canArchive(),
      canDelete: this.canDelete(),
    });
    if (lifecycle.length) {
      itens.push(
        { separator: true },
        ...lifecycle.map<ActionMenuItem>(option => ({
          label: option.label,
          icon: option.icon,
          command: () => this.lifecycleAction.emit(option.action),
        }))
      );
    }
    return itens;
  });
}
